import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/lib/auth/context";
import { useApplyPrefs } from "@/lib/hooks/useApplyPrefs";
import { Splash } from "@/components/shell/Splash";
import { ScrollToTop } from "@/components/shell/ScrollToTop";

// Pre-load: pierwszy render PWA (auth gate, splash, redirect)
import Welcome from "@/routes/Welcome";
import Login from "@/routes/Login";
import Dashboard from "@/routes/Dashboard";

// Lazy: oddzielne chunki dla cięższych / rzadziej używanych ścieżek.
// Initial bundle spada o ~30-40%, kazda lazy-route to osobny fetch on-demand.
const Signup = lazy(() => import("@/routes/Signup"));
const Onboarding = lazy(() => import("@/routes/Onboarding"));
const AuthAction = lazy(() => import("@/routes/AuthAction"));
const Privacy = lazy(() => import("@/routes/Privacy"));
const Terms = lazy(() => import("@/routes/Terms"));
const Chores = lazy(() => import("@/routes/Chores"));
const ChoreNew = lazy(() => import("@/routes/ChoreNew"));
const ChoreEdit = lazy(() => import("@/routes/ChoreEdit"));
const ChoresWeek = lazy(() => import("@/routes/ChoresWeek"));
const Approvals = lazy(() => import("@/routes/Approvals"));
const Wallet = lazy(() => import("@/routes/Wallet"));
const WalletDetail = lazy(() => import("@/routes/WalletDetail"));
const Stats = lazy(() => import("@/routes/Stats"));
const Settings = lazy(() => import("@/routes/Settings"));
const DevMascotLibrary = lazy(() => import("@/routes/DevMascotLibrary"));
const DevTreasureLibrary = lazy(() => import("@/routes/DevTreasureLibrary"));
// Sianko-level admin panel — lazy żeby bundle dla zwykłych userów nie
// ściągał kodu którego i tak nigdy nie zobaczą. Cloud Function gating
// (404 dla non-superAdmin) chroni dane niezależnie od routingu klienta.
const Admin = lazy(() => import("@/routes/Admin"));

function HomeRedirect() {
  const { loading, firebaseUser, userDoc } = useAuth();
  if (loading) return <Splash />;
  if (!firebaseUser || !userDoc) return <Navigate to="/welcome" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppLayout() {
  return (
    <AuthGuard>
      <AppShell>
        <Outlet />
      </AppShell>
    </AuthGuard>
  );
}

export default function App() {
  // Aplikuje motyw + akcent z user prefs (Firestore) lub localStorage fallback
  // dopóki user docu jeszcze nie ma. Na każdej stronie, też publicznych.
  useApplyPrefs();
  return (
    <Suspense fallback={<Splash />}>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/auth-action" element={<AuthAction />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/onboarding" element={<Onboarding />} />
        {import.meta.env.DEV && (
          <Route path="/dev/treasure-library" element={<DevTreasureLibrary />} />
        )}
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/chores" element={<Chores />} />
          <Route path="/chores/new" element={<ChoreNew />} />
          <Route path="/chores/week" element={<ChoresWeek />} />
          <Route path="/chores/:id/edit" element={<ChoreEdit />} />
          <Route path="/approvals" element={<Approvals />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/wallet/:uid" element={<WalletDetail />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin" element={<Admin />} />
          {import.meta.env.DEV && (
            <Route path="/dev/mascot-library" element={<DevMascotLibrary />} />
          )}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
